import { BrowserHelper } from "../browserHelper";
import { Camera } from "../camera";
import { CameraAccess } from "../cameraAccess";
import { CameraSettings } from "../cameraSettings";
import { CustomError } from "../customError";
import { GUI } from "./gui";

export enum MeteringMode {
  CONTINUOUS = "continuous",
  MANUAL = "manual",
  NONE = "none",
  SINGLE_SHOT = "single-shot",
}

export interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  focusMode?: MeteringMode[];
  torch?: boolean;
  zoom?: {
    max: number;
    min: number;
    step: number;
  };
}

export interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
  zoom?: number;
}

/**
 * A barcode picker utility class used to handle camera interaction.
 */
export class CameraManager {
  private static readonly cameraAccessTimeoutMs: number = 4000;
  private static readonly videoMetadataCheckTimeoutMs: number = 4000;
  private static readonly videoMetadataCheckIntervalMs: number = 50;
  private static readonly getCapabilitiesTimeoutMs: number = 500;
  private static readonly autofocusIntervalMs: number = 1500;
  private static readonly manualToAutofocusResumeTimeoutMs: number = 5000;
  private static readonly manualFocusWaitTimeoutMs: number = 400;
  private static readonly noCameraErrorParameters: { name: string; message: string } = {
    name: "NoCameraAvailableError",
    message: "No camera available",
  };
  private static readonly notReadableErrorParameters: { name: string; message: string } = {
    name: "NotReadableError",
    message: "Could not initialize camera correctly",
  };

  public selectedCamera?: Camera;
  public activeCamera?: Camera;
  public activeCameraSettings?: CameraSettings;

  private readonly triggerFatalError: (error: Error) => void;
  private readonly gui: GUI;
  private readonly postStreamInitializationListener: () => void = this.postStreamInitialization.bind(this);
  private readonly videoResizeListener: () => void = this.videoResizeHandle.bind(this);
  private readonly videoTrackEndedListener: () => void = this.videoTrackEndedRecovery.bind(this);
  private readonly videoTrackMuteListener: () => void = this.videoTrackMuteRecovery.bind(this);
  private readonly triggerManualFocusListener: () => void = this.triggerManualFocus.bind(this);
  private readonly triggerZoomStartListener: () => void = this.triggerZoomStart.bind(this);
  private readonly triggerZoomMoveListener: () => void = this.triggerZoomMove.bind(this);

  private cameraType: Camera.Type;
  private selectedCameraSettings?: CameraSettings;
  private mediaStream?: MediaStream;
  private mediaTrackCapabilities?: ExtendedMediaTrackCapabilities;
  private cameraAccessTimeout: number;
  private videoMetadataCheckInterval: number;
  private getCapabilitiesTimeout: number;
  private autofocusInterval: number;
  private manualToAutofocusResumeTimeout: number;
  private manualFocusWaitTimeout: number;
  private cameraSwitcherEnabled: boolean;
  private torchToggleEnabled: boolean;
  private tapToFocusEnabled: boolean;
  private pinchToZoomEnabled: boolean;
  private pinchToZoomDistance?: number;
  private pinchToZoomInitialZoom: number;
  private torchEnabled: boolean;
  private cameraInitializationPromise?: Promise<void>;

  constructor(triggerFatalError: (error: Error) => void, gui: GUI) {
    this.triggerFatalError = triggerFatalError;
    this.gui = gui;
    this.cameraType = Camera.Type.BACK;
  }

  public setInteractionOptions(
    cameraSwitcherEnabled: boolean,
    torchToggleEnabled: boolean,
    tapToFocusEnabled: boolean,
    pinchToZoomEnabled: boolean
  ): void {
    this.cameraSwitcherEnabled = cameraSwitcherEnabled;
    this.torchToggleEnabled = torchToggleEnabled;
    this.tapToFocusEnabled = tapToFocusEnabled;
    this.pinchToZoomEnabled = pinchToZoomEnabled;
  }

  public isCameraSwitcherEnabled(): boolean {
    return this.cameraSwitcherEnabled;
  }

  public async setCameraSwitcherEnabled(enabled: boolean): Promise<void> {
    this.cameraSwitcherEnabled = enabled;

    if (this.cameraSwitcherEnabled) {
      const cameras: Camera[] = await CameraAccess.getCameras();
      if (cameras.length > 1) {
        this.gui.setCameraSwitcherVisible(true);
      }
    } else {
      this.gui.setCameraSwitcherVisible(false);
    }
  }

  public isTorchToggleEnabled(): boolean {
    return this.torchToggleEnabled;
  }

  public setTorchToggleEnabled(enabled: boolean): void {
    this.torchToggleEnabled = enabled;

    if (this.torchToggleEnabled) {
      if (this.mediaStream != null && this.mediaTrackCapabilities?.torch === true) {
        this.gui.setTorchTogglerVisible(true);
      }
    } else {
      this.gui.setTorchTogglerVisible(false);
    }
  }

  public isTapToFocusEnabled(): boolean {
    return this.tapToFocusEnabled;
  }

  public setTapToFocusEnabled(enabled: boolean): void {
    this.tapToFocusEnabled = enabled;

    if (this.mediaStream != null) {
      if (this.tapToFocusEnabled) {
        this.enableTapToFocusListeners();
      } else {
        this.disableTapToFocusListeners();
      }
    }
  }

  public isPinchToZoomEnabled(): boolean {
    return this.pinchToZoomEnabled;
  }

  public setPinchToZoomEnabled(enabled: boolean): void {
    this.pinchToZoomEnabled = enabled;

    if (this.mediaStream != null) {
      if (this.pinchToZoomEnabled) {
        this.enablePinchToZoomListeners();
      } else {
        this.disablePinchToZoomListeners();
      }
    }
  }

  public setInitialCameraType(cameraType: Camera.Type): void {
    this.cameraType = cameraType;
  }

  public async setCameraType(cameraType: Camera.Type): Promise<void> {
    this.setInitialCameraType(cameraType);
    const mainCameraForType: Camera | undefined = CameraAccess.getMainCameraForType(
      await CameraAccess.getCameras(),
      cameraType
    );
    if (mainCameraForType != null && mainCameraForType !== this.selectedCamera) {
      return this.initializeCameraWithSettings(mainCameraForType, this.selectedCameraSettings);
    }
  }

  public setSelectedCamera(camera?: Camera): void {
    this.selectedCamera = camera;
  }

  public setSelectedCameraSettings(cameraSettings?: CameraSettings): void {
    this.selectedCameraSettings = cameraSettings;
  }

  public async setupCameras(): Promise<void> {
    if (this.cameraInitializationPromise != null) {
      return this.cameraInitializationPromise;
    }

    const mediaStreamTrack: MediaStreamTrack | void = await this.accessInitialCamera();
    const cameras: Camera[] = await CameraAccess.getCameras();

    if (this.cameraSwitcherEnabled && cameras.length > 1) {
      this.gui.setCameraSwitcherVisible(true);
    }

    if (mediaStreamTrack != null) {
      // We successfully accessed a camera, check if it's really the correct (main with wanted type or only) camera
      const wantedCamera: Camera | undefined = CameraAccess.adjustCamerasFromCameraStream(
        mediaStreamTrack,
        cameras,
        this.cameraType
      );
      if (wantedCamera == null) {
        console.debug("Initial camera access was incorrect, change camera");
      } else {
        console.debug("Initial camera access was correct, keep camera:", wantedCamera);
        this.setSelectedCamera(wantedCamera);
        this.updateActiveCameraCurrentResolution(wantedCamera);
        // Due to non-standard behaviour, it could happen that the stream got interrupted while getting the list of
        // cameras, this isn't handled by the existing "ended" event listener as the active camera wasn't set until
        // before this, so manually reinitialize camera if needed
        if (mediaStreamTrack.readyState === "ended") {
          return this.reinitializeCamera();
        }

        return;
      }
    }

    if (this.selectedCamera == null) {
      return this.accessAutoselectedCamera(cameras);
    } else {
      return this.initializeCameraWithSettings(this.selectedCamera, this.selectedCameraSettings);
    }
  }

  public stopStream(): void {
    if (this.activeCamera != null) {
      this.activeCamera.currentResolution = undefined;
    }

    this.activeCamera = undefined;

    if (this.mediaStream != null) {
      console.debug("Stop camera video stream access (stream):", this.mediaStream);
      window.clearTimeout(this.cameraAccessTimeout);
      window.clearInterval(this.videoMetadataCheckInterval);
      window.clearTimeout(this.getCapabilitiesTimeout);
      window.clearTimeout(this.manualFocusWaitTimeout);
      window.clearTimeout(this.manualToAutofocusResumeTimeout);
      window.clearInterval(this.autofocusInterval);
      this.mediaStream.getVideoTracks().forEach((track) => {
        track.removeEventListener("ended", this.videoTrackEndedListener);
        track.stop();
      });
      this.gui.videoElement.srcObject = null;
      this.mediaStream = undefined;
      this.mediaTrackCapabilities = undefined;
    }
  }

  public async applyCameraSettings(cameraSettings?: CameraSettings): Promise<void> {
    this.selectedCameraSettings = cameraSettings;

    if (this.activeCamera == null) {
      throw new CustomError(CameraManager.noCameraErrorParameters);
    }

    return this.initializeCameraWithSettings(this.activeCamera, cameraSettings);
  }

  public async reinitializeCamera(): Promise<void> {
    if (this.activeCamera == null) {
      // If the initial camera isn't active yet, do nothing: if and when the camera is later confirmed to be the correct
      // (main with wanted type or only) one this method will be called again after the camera is set to be active
      console.debug("Camera reinitialization delayed");
    } else {
      console.debug("Reinitialize camera:", this.activeCamera);
      try {
        await this.initializeCameraWithSettings(this.activeCamera, this.activeCameraSettings);
      } catch (error) {
        console.warn("Couldn't access camera:", this.activeCamera, error);
        this.triggerFatalError(error);
        throw error;
      }
    }
  }

  public async initializeCameraWithSettings(camera: Camera, cameraSettings?: CameraSettings): Promise<void> {
    if (this.cameraInitializationPromise != null) {
      await this.cameraInitializationPromise;
    }
    this.setSelectedCamera(camera);
    this.selectedCameraSettings = this.activeCameraSettings = cameraSettings;
    let resolutionFallbackLevel: number;
    switch (cameraSettings?.resolutionPreference) {
      case CameraSettings.ResolutionPreference.ULTRA_HD:
        resolutionFallbackLevel = 0;
        break;
      case CameraSettings.ResolutionPreference.FULL_HD:
        resolutionFallbackLevel = 1;
        break;
      case CameraSettings.ResolutionPreference.HD:
      default:
        resolutionFallbackLevel = 2;
        break;
    }
    this.cameraInitializationPromise = this.initializeCameraAndCheckUpdatedSettings(camera, resolutionFallbackLevel);

    return this.cameraInitializationPromise;
  }

  public async setTorchEnabled(enabled: boolean): Promise<void> {
    if (this.mediaStream != null && this.mediaTrackCapabilities?.torch === true) {
      this.torchEnabled = enabled;
      const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
      // istanbul ignore else
      if (videoTracks.length !== 0 && typeof videoTracks[0].applyConstraints === "function") {
        await videoTracks[0].applyConstraints({ advanced: <ExtendedMediaTrackConstraintSet[]>[{ torch: enabled }] });
      }
    }
  }

  public async toggleTorch(): Promise<void> {
    this.torchEnabled = !this.torchEnabled;
    await this.setTorchEnabled(this.torchEnabled);
  }

  public async setZoom(zoomPercentage: number, currentZoom?: number): Promise<void> {
    if (this.mediaStream != null && this.mediaTrackCapabilities?.zoom != null) {
      const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
      // istanbul ignore else
      if (videoTracks.length !== 0 && typeof videoTracks[0].applyConstraints === "function") {
        const zoomRange: number = this.mediaTrackCapabilities.zoom.max - this.mediaTrackCapabilities.zoom.min;
        const targetZoom: number = Math.max(
          this.mediaTrackCapabilities.zoom.min,
          Math.min(
            (currentZoom ?? this.mediaTrackCapabilities.zoom.min) + zoomRange * zoomPercentage,
            this.mediaTrackCapabilities.zoom.max
          )
        );
        await videoTracks[0].applyConstraints({
          advanced: <ExtendedMediaTrackConstraintSet[]>[{ zoom: targetZoom }],
        });
      }
    }
  }

  private async accessAutoselectedCamera(cameras: Camera[]): Promise<void> {
    cameras = CameraAccess.sortCamerasForCameraType(cameras, this.cameraType);

    let autoselectedCamera: Camera | undefined = cameras.shift();
    while (autoselectedCamera != null) {
      try {
        return await this.initializeCameraWithSettings(autoselectedCamera, this.selectedCameraSettings);
      } catch (error) {
        this.setSelectedCamera();
        if (cameras.length === 1) {
          this.gui.setCameraSwitcherVisible(false);
        }
        if (cameras.length >= 1) {
          console.warn("Couldn't access camera:", autoselectedCamera, error);
          autoselectedCamera = cameras.shift();
          continue;
        }
        throw error;
      }
    }
    throw new CustomError(CameraManager.noCameraErrorParameters);
  }

  private async accessInitialCamera(): Promise<MediaStreamTrack | void> {
    if (this.selectedCamera == null) {
      const primaryCamera: Camera = {
        deviceId: "",
        label: "",
        cameraType: this.cameraType,
      };

      try {
        await this.initializeCameraWithSettings(primaryCamera, this.selectedCameraSettings);
        if (this.mediaStream != null) {
          const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
          if (videoTracks.length !== 0) {
            return videoTracks[0];
          }
        }
      } catch {
        // Ignored
      } finally {
        this.setSelectedCamera();
      }
    }
  }

  private updateActiveCameraCurrentResolution(camera: Camera): void {
    if (this.gui.videoElement.videoWidth > 2 && this.gui.videoElement.videoHeight > 2) {
      camera.currentResolution = {
        width: this.gui.videoElement.videoWidth,
        height: this.gui.videoElement.videoHeight,
      };
    }
    // If it's the initial camera, do nothing: if and when the camera is later confirmed to be the
    // correct (main with wanted type or only) one this method will be called again with the right camera object
    if (camera.deviceId !== "") {
      this.activeCamera = camera;
      this.gui.setMirrorImageEnabled(this.gui.isMirrorImageEnabled(), false);
    }
  }

  private postStreamInitialization(): void {
    window.clearTimeout(this.getCapabilitiesTimeout);
    this.getCapabilitiesTimeout = window.setTimeout(() => {
      this.storeStreamCapabilities();
      this.setupAutofocus();
      if (this.torchToggleEnabled && this.mediaStream != null && this.mediaTrackCapabilities?.torch === true) {
        this.gui.setTorchTogglerVisible(true);
      }
    }, CameraManager.getCapabilitiesTimeoutMs);
  }

  private videoResizeHandle(): void {
    if (this.activeCamera != null) {
      this.updateActiveCameraCurrentResolution(this.activeCamera);
    }
  }

  private async videoTrackEndedRecovery(): Promise<void> {
    try {
      console.debug('Detected video track "ended" event, try to reinitialize camera');
      await this.reinitializeCamera();
    } catch {
      // Ignored
    }
  }

  private async videoTrackMuteRecovery(): Promise<void> {
    try {
      console.debug('Detected video track "unmute" event, try to reinitialize camera');
      await this.reinitializeCamera();
    } catch {
      // Ignored
    }
  }

  private async triggerManualFocusForContinuous(): Promise<void> {
    this.manualToAutofocusResumeTimeout = window.setTimeout(async () => {
      await this.triggerFocusMode(MeteringMode.CONTINUOUS);
    }, CameraManager.manualToAutofocusResumeTimeoutMs);

    await this.triggerFocusMode(MeteringMode.CONTINUOUS);
    this.manualFocusWaitTimeout = window.setTimeout(async () => {
      await this.triggerFocusMode(MeteringMode.MANUAL);
    }, CameraManager.manualFocusWaitTimeoutMs);
  }

  private async triggerManualFocusForSingleShot(): Promise<void> {
    window.clearInterval(this.autofocusInterval);

    this.manualToAutofocusResumeTimeout = window.setTimeout(() => {
      this.autofocusInterval = window.setInterval(this.triggerAutoFocus.bind(this), CameraManager.autofocusIntervalMs);
    }, CameraManager.manualToAutofocusResumeTimeoutMs);

    try {
      await this.triggerFocusMode(MeteringMode.SINGLE_SHOT);
    } catch {
      // istanbul ignore next
    }
  }

  private async triggerManualFocus(event?: MouseEvent | TouchEvent): Promise<void> {
    if (event != null) {
      event.preventDefault();
      if (event.type === "touchend" && (<TouchEvent>event).touches.length !== 0) {
        return;
      }
      // Check if we were using pinch-to-zoom
      if (this.pinchToZoomDistance != null) {
        this.pinchToZoomDistance = undefined;

        return;
      }
    }
    window.clearTimeout(this.manualFocusWaitTimeout);
    window.clearTimeout(this.manualToAutofocusResumeTimeout);
    if (this.mediaStream != null && this.mediaTrackCapabilities != null) {
      const focusModeCapability: MeteringMode[] | undefined = this.mediaTrackCapabilities.focusMode;
      if (focusModeCapability instanceof Array && focusModeCapability.includes(MeteringMode.SINGLE_SHOT)) {
        if (
          focusModeCapability.includes(MeteringMode.CONTINUOUS) &&
          focusModeCapability.includes(MeteringMode.MANUAL)
        ) {
          await this.triggerManualFocusForContinuous();
        } else if (!focusModeCapability.includes(MeteringMode.CONTINUOUS)) {
          await this.triggerManualFocusForSingleShot();
        }
      }
    }
  }

  private triggerZoomStart(event?: TouchEvent): void {
    if (event?.touches.length !== 2) {
      return;
    }
    event.preventDefault();
    this.pinchToZoomDistance = Math.hypot(
      (event.touches[1].screenX - event.touches[0].screenX) / screen.width,
      (event.touches[1].screenY - event.touches[0].screenY) / screen.height
    );
    if (this.mediaStream != null && this.mediaTrackCapabilities?.zoom != null) {
      const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
      // istanbul ignore else
      if (videoTracks.length !== 0 && typeof videoTracks[0].getConstraints === "function") {
        this.pinchToZoomInitialZoom = this.mediaTrackCapabilities.zoom.min;
        const currentConstraints: MediaTrackConstraints = videoTracks[0].getConstraints();
        if (currentConstraints.advanced != null) {
          const currentZoomConstraint: ExtendedMediaTrackConstraintSet | undefined = currentConstraints.advanced.find(
            (constraint) => {
              return "zoom" in constraint;
            }
          );
          if (currentZoomConstraint?.zoom != null) {
            this.pinchToZoomInitialZoom = currentZoomConstraint.zoom;
          }
        }
      }
    }
  }

  private async triggerZoomMove(event?: TouchEvent): Promise<void> {
    if (this.pinchToZoomDistance == null || event?.touches.length !== 2) {
      return;
    }
    event.preventDefault();
    await this.setZoom(
      (Math.hypot(
        (event.touches[1].screenX - event.touches[0].screenX) / screen.width,
        (event.touches[1].screenY - event.touches[0].screenY) / screen.height
      ) -
        this.pinchToZoomDistance) *
        2,
      this.pinchToZoomInitialZoom
    );
  }

  private storeStreamCapabilities(): void {
    // istanbul ignore else
    if (this.mediaStream != null) {
      const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
      // istanbul ignore else
      if (videoTracks.length !== 0 && typeof videoTracks[0].getCapabilities === "function") {
        this.mediaTrackCapabilities = videoTracks[0].getCapabilities();
      }
    }
  }

  private setupAutofocus(): void {
    window.clearTimeout(this.manualFocusWaitTimeout);
    window.clearTimeout(this.manualToAutofocusResumeTimeout);
    // istanbul ignore else
    if (this.mediaStream != null && this.mediaTrackCapabilities != null) {
      const focusModeCapability: MeteringMode[] | undefined = this.mediaTrackCapabilities.focusMode;
      if (
        focusModeCapability instanceof Array &&
        !focusModeCapability.includes(MeteringMode.CONTINUOUS) &&
        focusModeCapability.includes(MeteringMode.SINGLE_SHOT)
      ) {
        window.clearInterval(this.autofocusInterval);
        this.autofocusInterval = window.setInterval(
          this.triggerAutoFocus.bind(this),
          CameraManager.autofocusIntervalMs
        );
      }
    }
  }

  private async triggerAutoFocus(): Promise<void> {
    await this.triggerFocusMode(MeteringMode.SINGLE_SHOT);
  }

  private async triggerFocusMode(focusMode: MeteringMode): Promise<void> {
    // istanbul ignore else
    if (this.mediaStream != null) {
      const videoTracks: MediaStreamTrack[] = this.mediaStream.getVideoTracks();
      if (videoTracks.length !== 0 && typeof videoTracks[0].applyConstraints === "function") {
        try {
          await videoTracks[0].applyConstraints({ advanced: <MediaTrackConstraintSet[]>(<unknown>[{ focusMode }]) });
        } catch {
          // Ignored
        }
      }
    }
  }

  private enableTapToFocusListeners(): void {
    ["touchend", "mousedown"].forEach((eventName) => {
      this.gui.videoElement.addEventListener(eventName, this.triggerManualFocusListener);
    });
  }

  private enablePinchToZoomListeners(): void {
    this.gui.videoElement.addEventListener("touchstart", this.triggerZoomStartListener);
    this.gui.videoElement.addEventListener("touchmove", this.triggerZoomMoveListener);
  }

  private disableTapToFocusListeners(): void {
    ["touchend", "mousedown"].forEach((eventName) => {
      this.gui.videoElement.removeEventListener(eventName, this.triggerManualFocusListener);
    });
  }

  private disablePinchToZoomListeners(): void {
    this.gui.videoElement.removeEventListener("touchstart", this.triggerZoomStartListener);
    this.gui.videoElement.removeEventListener("touchmove", this.triggerZoomMoveListener);
  }

  private async initializeCameraAndCheckUpdatedSettings(
    camera: Camera,
    resolutionFallbackLevel: number
  ): Promise<void> {
    try {
      await this.initializeCamera(camera, resolutionFallbackLevel);
      // Check if due to asynchronous behaviour camera settings were changed while camera was initialized
      if (
        this.selectedCameraSettings !== this.activeCameraSettings &&
        (this.selectedCameraSettings == null ||
          this.activeCameraSettings == null ||
          (<(keyof CameraSettings)[]>Object.keys(this.selectedCameraSettings)).some((cameraSettingsProperty) => {
            return (
              (<CameraSettings>this.selectedCameraSettings)[cameraSettingsProperty] !==
              (<CameraSettings>this.activeCameraSettings)[cameraSettingsProperty]
            );
          }))
      ) {
        this.activeCameraSettings = this.selectedCameraSettings;

        return this.initializeCameraAndCheckUpdatedSettings(camera, resolutionFallbackLevel);
      }
    } finally {
      this.cameraInitializationPromise = undefined;
    }
  }

  private retryInitializeCameraIfNeeded(
    camera: Camera,
    resolutionFallbackLevel: number,
    error: Error
  ): Promise<void> | void {
    if (resolutionFallbackLevel < 4) {
      return this.initializeCamera(camera, resolutionFallbackLevel + 1);
    } else {
      throw error;
    }
  }

  private async handleCameraInitializationError(
    camera: Camera,
    resolutionFallbackLevel: number,
    error: Error
  ): Promise<void> {
    if (
      (error.message === "Invalid constraint" ||
        (error.name === "OverconstrainedError" && (<OverconstrainedError>error).constraint === "deviceId")) &&
      camera.label !== ""
    ) {
      // Camera might have changed deviceId: check for new cameras with same label and type but different deviceId
      const cameras: Camera[] = await CameraAccess.getCameras(true);
      const newCamera: Camera | undefined = cameras.find((currentCamera) => {
        return (
          currentCamera.label === camera.label &&
          currentCamera.cameraType === camera.cameraType &&
          currentCamera.deviceId !== camera.deviceId
        );
      });
      if (newCamera == null) {
        return this.retryInitializeCameraIfNeeded(camera, resolutionFallbackLevel, error);
      } else {
        return this.initializeCamera(newCamera, resolutionFallbackLevel);
      }
    }
    if (["AbortError", "NotAllowedError", "NotFoundError", "NotReadableError", "SecurityError"].includes(error.name)) {
      // Camera is not accessible at all
      throw error;
    }

    return this.retryInitializeCameraIfNeeded(camera, resolutionFallbackLevel, error);
  }

  private async initializeCamera(camera: Camera, resolutionFallbackLevel: number): Promise<void> {
    if (camera == null) {
      throw new CustomError(CameraManager.noCameraErrorParameters);
    }
    this.stopStream();
    this.torchEnabled = false;
    this.gui.setTorchTogglerVisible(false);

    try {
      const stream: MediaStream = await CameraAccess.accessCameraStream(resolutionFallbackLevel, camera);
      // Detect weird browser behaviour that on unsupported resolution returns a 2x2 video instead
      if (typeof stream.getTracks()[0].getSettings === "function") {
        const mediaTrackSettings: MediaTrackSettings = stream.getTracks()[0].getSettings();
        if (
          mediaTrackSettings.width != null &&
          mediaTrackSettings.height != null &&
          (mediaTrackSettings.width === 2 || mediaTrackSettings.height === 2)
        ) {
          if (resolutionFallbackLevel === 4) {
            throw new CustomError(CameraManager.notReadableErrorParameters);
          } else {
            return this.initializeCamera(camera, resolutionFallbackLevel + 1);
          }
        }
      }
      this.mediaStream = stream;
      this.mediaStream.getVideoTracks().forEach((track) => {
        // Handle unexpected stream end events
        track.addEventListener("ended", this.videoTrackEndedListener);
        if (BrowserHelper.userAgentInfo.getBrowser().name === "Safari") {
          // Safari only allows a single page to have the camera active at any time, if the track gets muted we need
          // to reinitialize the camera to access it again (this is done automatically only once the page is visible)
          // as soon as possible, other browsers use mute/unmute on page visibility changes, but not Safari.
          // This will add the listeners only once in case of multiple calls: identical listeners are ignored
          track.addEventListener("mute", this.videoTrackMuteListener);
        }
      });

      return this.setupCameraStreamVideo(camera, stream);
    } catch (error) {
      return this.handleCameraInitializationError(camera, resolutionFallbackLevel, error);
    }
  }

  private async checkCameraAccess(camera: Camera): Promise<void> {
    window.clearTimeout(this.cameraAccessTimeout);

    return new Promise((_, reject) => {
      this.cameraAccessTimeout = window.setTimeout(() => {
        console.debug("Camera video stream access failure (video data load timeout):", camera);
        this.stopStream();
        reject(new CustomError(CameraManager.notReadableErrorParameters));
      }, CameraManager.cameraAccessTimeoutMs);
    });
  }

  private async checkVideoMetadata(camera: Camera): Promise<void> {
    return new Promise((resolve, reject) => {
      this.gui.videoElement.onloadeddata = () => {
        this.gui.videoElement.onloadeddata = null;
        window.clearTimeout(this.cameraAccessTimeout);

        // Detect weird browser behaviour that on unsupported resolution returns a 2x2 video instead
        // Also detect failed camera access with no error but also no video stream provided
        if (
          this.gui.videoElement.videoWidth > 2 &&
          this.gui.videoElement.videoHeight > 2 &&
          this.gui.videoElement.currentTime > 0
        ) {
          this.updateActiveCameraCurrentResolution(camera);
          console.debug("Camera video stream access success:", camera);

          return resolve();
        }

        const videoMetadataCheckStartTime: number = performance.now();

        window.clearInterval(this.videoMetadataCheckInterval);
        this.videoMetadataCheckInterval = window.setInterval(() => {
          // Detect weird browser behaviour that on unsupported resolution returns a 2x2 video instead
          // Also detect failed camera access with no error but also no video stream provided
          if (
            this.gui.videoElement.videoWidth <= 2 ||
            this.gui.videoElement.videoHeight <= 2 ||
            this.gui.videoElement.currentTime === 0
          ) {
            if (performance.now() - videoMetadataCheckStartTime > CameraManager.videoMetadataCheckTimeoutMs) {
              console.debug("Camera video stream access failure (valid video metadata timeout):", camera);
              window.clearInterval(this.videoMetadataCheckInterval);
              this.stopStream();

              return reject(new CustomError(CameraManager.notReadableErrorParameters));
            }

            return;
          }

          window.clearInterval(this.videoMetadataCheckInterval);
          this.updateActiveCameraCurrentResolution(camera);
          console.debug("Camera video stream access success:", camera);

          resolve();
        }, CameraManager.videoMetadataCheckIntervalMs);
      };
    });
  }

  private setupCameraStreamVideo(camera: Camera, stream: MediaStream): Promise<void> {
    // These will add the listeners only once in the case of multiple calls, identical listeners are ignored
    this.gui.videoElement.addEventListener("loadedmetadata", this.postStreamInitializationListener);
    this.gui.videoElement.addEventListener("resize", this.videoResizeListener);

    if (this.tapToFocusEnabled) {
      this.enableTapToFocusListeners();
    }
    if (this.pinchToZoomEnabled) {
      this.enablePinchToZoomListeners();
    }

    const cameraStreamVideoCheck: Promise<void> = Promise.race([
      this.checkCameraAccess(camera),
      this.checkVideoMetadata(camera),
    ]);

    this.gui.videoElement.srcObject = stream;
    this.gui.videoElement.load();
    this.gui.playVideo();

    return cameraStreamVideoCheck;
  }
}
